import { getApiClient } from "@/services"
import {
  BOOK_CAREADVOCATE_CALLBACK_SLOT_ACTION,
  CHOOSE_CAREADVOCATE_CALLBACK_TO_CANCEL_ACTION,
  CHOOSE_CAREADVOCATE_CALLBACK_TO_RESCHEDULE_ACTION,
  RESCHEDULE_CAREADVOCATE_CALLBACK_SLOT_ACTION,
  createAgentTextMessage,
  createErrorMessage,
  createToolCallMessage,
  createUserMessage,
  type Message,
  type MessageSegment,
  messageText,
} from "@/types"
import { useCallback, useEffect, useReducer, useRef, useState } from "react"

import { CARE_ADVOCATE_PHONE_LINK, PENDO_AGENT_ID } from "@/utils/constants"
import { chatReducer, initialState } from "./useChat.reducer"
import { usePendo } from "./usePendo"

export const GENERIC_ERROR_MESSAGE =
  "Apologies — something’s not working on my end. Give it another try, or if you’d " +
  `rather not wait, a Care Advocate is available at ${CARE_ADVOCATE_PHONE_LINK} or via Live Chat.`

export const NON_RETRYABLE_ERROR_MESSAGE =
  "Apologies — something’s not working on my end. A Care Advocate is available to help " +
  `at ${CARE_ADVOCATE_PHONE_LINK} or via Live Chat.`

interface UseChatOptions {
  getAuthToken?: () => string | null;
  onWidgetAction?: (action: string, data: Record<string, unknown>) => void;
  enabled?: boolean;
}

interface UseChatReturn {
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  threadId: string | null;
  sendMessage: (content: string) => Promise<void>;
  clearMessages: () => void;
  handleWidgetAction: (action: string, data: Record<string, unknown>) => void;
  abortStream: () => void;
  voteOnMessage: (
    messageId: string,
    wasHelpful: boolean | null,
  ) => Promise<void>;
  isVoting: boolean;
}

/**
 * Hook for managing chat state and interactions with streaming support
 */
export function useChat(options: UseChatOptions = {}): UseChatReturn {
  const { getAuthToken = () => null, onWidgetAction, enabled = true } = options
  const [state, dispatch] = useReducer(chatReducer, initialState)
  const apiClientRef = useRef(getApiClient(getAuthToken))
  const abortControllerRef = useRef<AbortController | null>(null)
  const hasInitializedRef = useRef(false)
  const { trackAgent } = usePendo()

  // Create thread on load and display greeting
  useEffect(() => {
    if (!enabled || hasInitializedRef.current) return
    hasInitializedRef.current = true

    const initThread = async () => {
      dispatch({ type: "SET_LOADING", payload: true })
      try {
        const thread = await apiClientRef.current.createThread()
        dispatch({ type: "SET_THREAD_ID", payload: thread.id })
        if (thread.greeting) {
          dispatch({
            type: "ADD_MESSAGE",
            payload: {
              ...createAgentTextMessage([
                { type: "text", content: thread.greeting },
              ]),
              suggestions: thread.greeting_suggestions,
            },
          })
        }
      } catch (error) {
        console.error("Failed to initialize chat thread:", error)

        dispatch({
          type: "ADD_MESSAGE",
          payload: createErrorMessage(NON_RETRYABLE_ERROR_MESSAGE),
        })
      } finally {
        dispatch({ type: "SET_LOADING", payload: false })
      }
    }

    initThread()
  }, [enabled])

  const sendMessage = useCallback(
    async (content: string) => {
      // Don't send empty messages
      if (!content.trim()) return

      // Thread should be created on init; bail if not ready
      if (!state.threadId) {
        dispatch({
          type: "ADD_MESSAGE",
          payload: createErrorMessage(NON_RETRYABLE_ERROR_MESSAGE),
        })
        return
      }

      // Abort any existing stream
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      // Held locally as well as on the ref so this turn's teardown can tell
      // whether the ref still belongs to it — tapping a widget mid-stream
      // starts the next turn before this one finishes unwinding.
      const abortController = new AbortController()
      abortControllerRef.current = abortController

      // Add user message immediately
      const userMessage = createUserMessage(content)
      dispatch({ type: "ADD_MESSAGE", payload: userMessage })
      dispatch({ type: "SET_LOADING", payload: true })

      // Hoisted so the catch/AbortError branch can remove the in-progress
      // bubble if the stream is aborted after first content was rendered.
      // Reassigned at each tool-call boundary so every tool turn gets its own
      // bubble (see the `tool_call` case).
      let assistantId = crypto.randomUUID()
      let bubbleCreated = false
      // Hoisted with assistantId/bubbleCreated so the catch branch can tell
      // whether a retract-to-0 emptied the bubble before the stream threw.
      const segments: MessageSegment[] = []
      // Transient messages added mid-turn that must be cleaned up on abort:
      // tool-call chips (otherwise a "Searching…" chip pulses forever with no
      // resolution coming) and sealed preamble bubbles from earlier tool
      // iterations (otherwise they orphan above the removed active bubble).
      const toolCallIds: string[] = []
      const staleBubbleIds: string[] = []
      // Set once the turn's answer is finalized (persisted, bubble committed).
      // The stream stays open past `complete` for the DB commit + suggestions
      // LLM call, so an abort here must NOT tear down the answer the member is
      // already reading — the abort cleanup is skipped when this is true.
      let finalized = false

      try {
        const threadId = state.threadId

        // Track prompt event
        const promptMessageId = crypto.randomUUID()
        trackAgent("prompt", {
          agentId: PENDO_AGENT_ID,
          conversationId: threadId,
          messageId: promptMessageId,
          content,
        })

        // Stream the response
        const stream = apiClientRef.current.sendMessageStream(
          threadId,
          content,
          abortController.signal,
        )

        let serverId: string | undefined
        let hadError = false

        // The in-progress assistant bubble as ordered segments. Two producers,
        // never mixed within one turn:
        //  • buffered / segments path (flag off): `text` (one per part) +
        //    `widget` events, each a complete segment pushed directly.
        //  • streaming-governor path (flag on): `text_delta` per approved
        //    sentence and `widget` events at their marker positions, replayed
        //    from `streamItems` into the tail of `segments`; plus `retract` +
        //    one `text` substitute on a mid-stream reject.
        // The bubble is created lazily on first content (upsertBubble) so an
        // error-only/empty turn leaves no blank bubble. (assistantId +
        // bubbleCreated are hoisted above the try for the catch branch.)
        type StreamItem =
          | { kind: "text"; body: string }
          | { kind: "widget"; segment: MessageSegment }
        const streamItems: StreamItem[] = []
        let streamBase = 0 // segments[] index where the replayed tail begins
        let retracted = false // a mid-stream reject happened this turn
        // Latched on first text_delta — the streaming-governor signature. Buffered
        // turns emit only text/widget, so this stays false and the UI renders
        // them once at `complete` instead of live.
        let sawDelta = false

        const upsertBubble = () => {
          dispatch({
            type: "UPSERT_STREAMING_MESSAGE",
            payload: {
              id: assistantId,
              segments: segments.map((s) => ({ ...s })),
              streamed: sawDelta,
            },
          })
          bubbleCreated = true
        }

        // Replay the stream buffer into the tail of segments (after
        // streamBase): consecutive deltas coalesce into one text segment,
        // widgets render as their own segments at their arrival position.
        const rebuildStreamedSegments = () => {
          segments.length = streamBase
          let textRun = ""
          const flushTextRun = () => {
            if (textRun) {
              segments.push({ type: "text", content: textRun })
              textRun = ""
            }
          }
          for (const item of streamItems) {
            if (item.kind === "text") {
              textRun += item.body
            } else {
              flushTextRun()
              segments.push({ ...item.segment })
            }
          }
          flushTextRun()
        }

        for await (const event of stream) {
          switch (event.t) {
            case "text":
              // A complete text part. In the streaming path this only arrives
              // as a post-retract substitute — the full regenerated answer,
              // which replaces everything streamed so far.
              if (retracted) {
                segments.length = 0
                streamItems.length = 0
                streamBase = 0
                retracted = false
              }
              segments.push({ type: "text", content: event.body })
              streamBase = segments.length // later deltas replay after it
              // Buffered turns accumulate and render once at `complete` (like
              // main), so flag-off stays a no-op; streaming turns render live.
              if (bubbleCreated || sawDelta) upsertBubble()
              break

            case "text_delta":
              sawDelta = true
              streamItems.push({ kind: "text", body: event.body })
              rebuildStreamedSegments()
              upsertBubble()
              break

            case "widget": {
              const widgetSegment: MessageSegment = {
                type: "widget",
                widgetType: event.body.widget_type,
                data: event.body.data,
              }
              if (sawDelta) {
                // Streaming turn: the widget rides at its marker position
                // between deltas, so it replays through the stream buffer
                // like the prose around it.
                streamItems.push({ kind: "widget", segment: widgetSegment })
                rebuildStreamedSegments()
                upsertBubble()
              } else {
                segments.push(widgetSegment)
                streamBase = segments.length
                if (bubbleCreated) upsertBubble()
              }
              break
            }

            case "retract":
              // The buffer, not the bubble, decides what a retract clears: a
              // widget arriving before the first delta holds a segment while
              // the bubble is still uncreated, and that widget is condemned
              // with the rest of the turn. Only the render is conditional.
              retracted = true
              if (event.body.from_index <= 0) {
                // From 0 condemns the whole answer: widgets and any parts
                // that arrived before the first delta clear too.
                segments.length = 0
                streamItems.length = 0
                streamBase = 0
              } else {
                // Drop the from-th delta onward, along with widgets that
                // arrived after it; widgets between surviving deltas stay.
                // Indices count text deltas only, matching the server's
                // emitted-delta count.
                let deltasSeen = 0
                let cutAt = streamItems.length
                for (let i = 0; i < streamItems.length; i++) {
                  if (streamItems[i].kind !== "text") continue
                  if (deltasSeen === event.body.from_index) {
                    cutAt = i
                    break
                  }
                  deltasSeen++
                }
                streamItems.splice(cutAt)
                rebuildStreamedSegments()
              }
              if (bubbleCreated) upsertBubble()
              break

            case "tool_call":
              // A tool call ends the streaming turn. Seal any preamble bubble
              // and start a fresh buffer so the post-tool answer renders below
              // the chip, and the next turn's retract indices (reset per turn
              // server-side) realign to an empty buffer, not the turn-global one.
              // The persisted preamble never carries widget data (its markers
              // consume none), so widget segments are dropped from the sealed
              // bubble — matching what a reload will show.
              // Keyed off the buffer rather than the bubble: a widget-only
              // preamble holds segments while the bubble is still uncreated,
              // and its widget and streamBase must not leak into the answer.
              if (streamItems.length > 0 || segments.length > 0) {
                const proseOnly = segments.filter(
                  (segment) => segment.type === "text",
                )
                if (proseOnly.length > 0) {
                  segments.length = 0
                  segments.push(...proseOnly)
                  upsertBubble()
                  staleBubbleIds.push(assistantId)
                } else if (bubbleCreated) {
                  // A widget-only preamble seals to nothing; drop the bubble.
                  dispatch({
                    type: "REMOVE_MESSAGE",
                    payload: { id: assistantId },
                  })
                }
                assistantId = crypto.randomUUID()
                segments.length = 0
                streamItems.length = 0
                streamBase = 0
                retracted = false
                sawDelta = false
                bubbleCreated = false
              }
              {
                const toolMessage = createToolCallMessage(event.body)
                toolCallIds.push(toolMessage.id)
                dispatch({ type: "ADD_MESSAGE", payload: toolMessage })
              }
              break

            case "complete":
              // Final event with message_id
              serverId = event.body.message_id?.toString()

              // Don't emit a bubble after an error event — complete is just a stream terminator
              if (!hadError) {
                const responseContent = messageText(segments)
                // A widget-only answer has no prose but is NOT empty — the
                // widget IS the response. Fall back to the error bubble only
                // when nothing is renderable (no text AND no widget segments).
                const hasRenderableSegments =
                  responseContent.trim() !== "" ||
                  segments.some((segment) => segment.type === "widget")
                if (!hasRenderableSegments) {
                  if (bubbleCreated) {
                    dispatch({
                      type: "REMOVE_MESSAGE",
                      payload: { id: assistantId },
                    })
                  }
                  dispatch({
                    type: "ADD_MESSAGE",
                    payload: createErrorMessage(NON_RETRYABLE_ERROR_MESSAGE),
                  })
                } else {
                  // Track agent response event
                  trackAgent("agent_response", {
                    agentId: PENDO_AGENT_ID,
                    conversationId: threadId,
                    messageId: serverId ?? crypto.randomUUID(),
                    content: responseContent,
                  })

                  // Buffered turns never rendered live; create the bubble now
                  // for one atomic render. Finalize then attaches serverId.
                  if (!bubbleCreated) upsertBubble()
                  dispatch({
                    type: "FINALIZE_STREAMING_MESSAGE",
                    payload: { id: assistantId, serverId },
                  })
                  // A real answer is committed and on screen. The stream lingers
                  // (DB commit + suggestions), so latch finalized + drop the
                  // tool-chip refs to keep a late abort from removing the
                  // finalized bubble or its chips. Only here: the error/empty
                  // branches above leave no answer, so their orphaned "Searching…"
                  // chips must still be cleaned up on abort.
                  finalized = true
                  toolCallIds.length = 0
                }
              }

              dispatch({ type: "SET_LOADING", payload: false })

              break

            case "suggestions":
              // Arrives after `complete`; attach chips to that message.
              if (event.body.chips.length > 0) {
                dispatch({
                  type: "SET_MESSAGE_SUGGESTIONS",
                  payload: {
                    serverId: event.body.message_id.toString(),
                    chips: event.body.chips,
                  },
                })
              }
              break

            case "error":
              hadError = true
              // A stream error is preceded by retract-to-0 (server clears the
              // condemned text), which empties the streaming bubble but leaves
              // it mounted. Remove the now-empty pill so the error card doesn't
              // sit under an orphaned blank bubble.
              if (bubbleCreated && segments.length === 0) {
                dispatch({
                  type: "REMOVE_MESSAGE",
                  payload: { id: assistantId },
                })
                bubbleCreated = false
              }
              dispatch({
                type: "ADD_MESSAGE",
                payload: createErrorMessage(event.body),
              })
              dispatch({ type: "SET_ERROR", payload: event.body })
              break
          }
        }
      } catch (error) {
        // Check if it was an abort
        if (error instanceof Error && error.name === "AbortError") {
          // An abort after `complete` finalized the turn: the answer is
          // persisted and on screen, so leave it (and its chips) intact.
          if (finalized) return
          if (bubbleCreated) {
            dispatch({
              type: "REMOVE_MESSAGE",
              payload: { id: assistantId },
            })
          }
          // Remove mid-turn transients that would otherwise be stranded: the
          // sealed preamble bubbles and the tool-call chips (a pulsing
          // "Searching…" chip with no turn left to resolve it).
          for (const id of [...staleBubbleIds, ...toolCallIds]) {
            dispatch({ type: "REMOVE_MESSAGE", payload: { id } })
          }
          return
        }

        console.error("Error processing message stream:", error)

        // Same orphan risk as the `error` event: if a retract-to-0 emptied the
        // streaming bubble just before the stream threw, remove the now-blank
        // pill so the error card doesn't sit under it.
        if (bubbleCreated && segments.length === 0) {
          dispatch({
            type: "REMOVE_MESSAGE",
            payload: { id: assistantId },
          })
          bubbleCreated = false
        }

        dispatch({
          type: "ADD_MESSAGE",
          payload: createErrorMessage(GENERIC_ERROR_MESSAGE),
        })
        dispatch({ type: "SET_ERROR", payload: GENERIC_ERROR_MESSAGE })
      } finally {
        // Only the turn that still owns the ref may end the loading state and
        // release it; a superseded turn unwinding here would otherwise stop the
        // replacement's spinner and strand it with no way to be aborted.
        if (abortControllerRef.current === abortController) {
          dispatch({ type: "SET_LOADING", payload: false })
          abortControllerRef.current = null
        }
      }
    },
    [state.threadId, trackAgent],
  )

  const clearMessages = useCallback(() => {
    // Abort any existing stream
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    dispatch({ type: "CLEAR_MESSAGES" })
  }, [])

  const abortStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
      dispatch({ type: "SET_LOADING", payload: false })
    }
  }, [])

  const handleWidgetAction = useCallback(
    (action: string, data: Record<string, unknown>) => {
      // A tapped callback slot becomes a plain booking message: the assistant
      // maps the label back to the slot's booking key from its tool context,
      // so no separate booking endpoint is needed.
      if (
        action === BOOK_CAREADVOCATE_CALLBACK_SLOT_ACTION &&
        typeof data.label === "string"
      ) {
        void sendMessage(`Book my Care Advocate call for ${data.label}`)
        return
      }

      // Moving a call reads as its own request rather than a second booking, so
      // the assistant reaches for the reschedule tool instead of the booking one.
      if (
        action === RESCHEDULE_CAREADVOCATE_CALLBACK_SLOT_ACTION &&
        typeof data.label === "string"
      ) {
        void sendMessage(
          typeof data.currentBookingLabel === "string" && data.currentBookingLabel
            ? `Move my Care Advocate call from ${data.currentBookingLabel} to ${data.label}`
            : `Reschedule my Care Advocate call to ${data.label}`,
        )
        return
      }

      // A tapped call names exactly which of the member's calls to move or
      // cancel; the assistant maps the label back to its callback id.
      if (
        action === CHOOSE_CAREADVOCATE_CALLBACK_TO_RESCHEDULE_ACTION &&
        typeof data.label === "string"
      ) {
        void sendMessage(
          `Move my Care Advocate call booked for ${data.label} to a different time`,
        )
        return
      }

      if (
        action === CHOOSE_CAREADVOCATE_CALLBACK_TO_CANCEL_ACTION &&
        typeof data.label === "string"
      ) {
        void sendMessage(`Cancel my Care Advocate call booked for ${data.label}`)
        return
      }

      // Call custom handler if provided
      onWidgetAction?.(action, data)
    },
    [onWidgetAction, sendMessage],
  )

  // Voting state and function
  const [isVoting, setIsVoting] = useState(false)

  // Ref lets voteOnMessage read current messages without depending on them
  const messagesRef = useRef(state.messages)
  messagesRef.current = state.messages

  const voteOnMessage = useCallback(
    async (messageId: string, wasHelpful: boolean | null) => {
      const message = messagesRef.current.find(
        (m) => m.id === messageId && m.type === "text" && m.sender === "agent",
      )
      if (!message || message.type !== "text" || !message.serverId) {
        console.warn("Cannot vote on message without server ID")
        return
      }

      if (!state.threadId) {
        console.warn("Cannot vote without thread ID")
        return
      }

      setIsVoting(true)
      try {
        await apiClientRef.current.voteOnMessage(
          state.threadId,
          message.serverId,
          wasHelpful,
        )
        dispatch({
          type: "SET_MESSAGE_VOTE",
          payload: { id: messageId, wasHelpful },
        })

        // Track user reaction event
        const reaction =
          wasHelpful === true
            ? "positive"
            : wasHelpful === false
              ? "negative"
              : "undo"
        trackAgent("user_reaction", {
          agentId: PENDO_AGENT_ID,
          conversationId: state.threadId!,
          messageId: message.serverId,
          content: reaction,
        })
      } catch (error) {
        console.error("Failed to vote on message:", error)
      } finally {
        setIsVoting(false)
      }
    },
    [state.threadId, trackAgent],
  )

  return {
    messages: state.messages,
    isLoading: state.isLoading,
    error: state.error,
    threadId: state.threadId,
    sendMessage,
    clearMessages,
    handleWidgetAction,
    abortStream,
    voteOnMessage,
    isVoting,
  }
}
